import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Download,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  RefreshCw,
  Share2,
  Trash2,
  UsersRound,
  X,
  createIcons,
} from "lucide";
import { QUESTIONS, QUIZ_LENGTH } from "../shared/questions";
import { buildPrivateManagementUrl, parseOwnerAccessFragment } from "../shared/owner-access";
import { ROLE_BY_ID, ROLE_IDS, ROLES, type RoleDefinition } from "../shared/roles";
import type { RawAnswer } from "../shared/scoring";
import type { CreateProfileResponse, PublicProfile, ResultsResponse } from "../shared/types";
import { ApiError, createProfile, deleteProfile, getPublicProfile, getResults, submitResponse } from "./api";
import "./styles.css";

interface QuizDraft {
  version: 1;
  nickname: string;
  consent: true;
  current: number;
  answers: Array<string | null>;
}

type Route =
  | { kind: "create" }
  | { kind: "participate"; slug: string }
  | { kind: "manage"; slug: string };

type AppState =
  | { mode: "loading"; route: Route }
  | { mode: "create"; submitting: boolean; error: string | null }
  | { mode: "invite"; created: CreateProfileResponse }
  | { mode: "participant-start"; profile: PublicProfile; nickname: string; consent: boolean; error: string | null }
  | { mode: "quiz"; profile: PublicProfile; draft: QuizDraft; stage: "answering" | "review" | "submitting" | "submit-error"; error: string | null }
  | { mode: "thanks"; profile: PublicProfile; alreadyDone: boolean }
  | { mode: "manage"; slug: string; results: ResultsResponse; feedback: string | null; deleteOpen: boolean; deleting: boolean }
  | { mode: "unauthorized"; slug: string }
  | { mode: "unavailable"; message: string }
  | { mode: "error"; route: Route; message: string };

const appRoot = document.querySelector<HTMLDivElement>("#app");
if (!appRoot) throw new Error("App root is missing.");
const app: HTMLDivElement = appRoot;

let state: AppState = { mode: "loading", route: readRoute() };
let choiceTimer: number | null = null;

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function readRoute(): Route {
  const params = new URLSearchParams(location.search);
  const participate = params.get("p");
  if (participate) return { kind: "participate", slug: participate };
  const manage = params.get("manage");
  if (manage) return { kind: "manage", slug: manage };
  return { kind: "create" };
}

function ownerKey(slug: string): string {
  return `friend-lens:owner:${slug}`;
}

function respondentKey(slug: string): string {
  return `friend-lens:respondent:${slug}`;
}

function doneKey(slug: string): string {
  return `friend-lens:done:${slug}`;
}

function draftKey(slug: string): string {
  return `friend-lens:draft:${slug}`;
}

function publicUrl(slug: string): string {
  return `${location.origin}/?p=${encodeURIComponent(slug)}`;
}

function privateManagementUrl(slug: string, ownerToken: string): string {
  return buildPrivateManagementUrl(location.origin, slug, ownerToken);
}

function consumeOwnerAccess(route: Route): void {
  if (route.kind !== "manage") return;
  const access = parseOwnerAccessFragment(location.hash);
  if (!access.present) return;
  history.replaceState(history.state, "", `${location.pathname}${location.search}`);
  if (access.token) localStorage.setItem(ownerKey(route.slug), access.token);
}

function makeLocalToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function getRespondentToken(slug: string): string {
  const existing = localStorage.getItem(respondentKey(slug));
  if (existing) return existing;
  const created = makeLocalToken();
  localStorage.setItem(respondentKey(slug), created);
  return created;
}

function readDraft(slug: string): QuizDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(slug));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<QuizDraft>;
    if (
      value.version !== 1 ||
      typeof value.nickname !== "string" ||
      value.consent !== true ||
      !Number.isInteger(value.current) ||
      !Array.isArray(value.answers) ||
      value.answers.length !== QUIZ_LENGTH ||
      !value.answers.every((answer) => answer === null || typeof answer === "string")
    ) return null;
    return value as QuizDraft;
  } catch {
    return null;
  }
}

function saveDraft(slug: string, draft: QuizDraft): void {
  sessionStorage.setItem(draftKey(slug), JSON.stringify(draft));
}

function clearChoiceTimer(): void {
  if (choiceTimer !== null) window.clearTimeout(choiceTimer);
  choiceTimer = null;
}

function rolesMosaic(): string {
  return `<div class="role-mosaic" aria-label="六种朋友角色">
    ${ROLES.map((role) => `<figure class="role-tile role-color--${role.id}">
      <img src="${role.image}" width="384" height="384" alt="${role.name}" />
      <figcaption>${role.name}</figcaption>
    </figure>`).join("")}
  </div>`;
}

function iconButton(action: string, icon: string, label: string): string {
  return `<button class="icon-button" type="button" data-action="${action}" aria-label="${label}" title="${label}"><i data-lucide="${icon}" aria-hidden="true"></i></button>`;
}

function shell(content: string, options: { back?: string; compact?: boolean } = {}): string {
  return `<div class="app-shell${options.compact ? " app-shell--compact" : ""}">
    <a class="skip-link" href="#main-content">跳到主要内容</a>
    <header class="topbar">
      ${options.back ? iconButton(options.back, "arrow-left", "返回") : '<span class="topbar-mark" aria-hidden="true">镜头</span>'}
      <a class="brand-link" href="/" data-action="go-create">朋友镜头里的我</a>
    </header>
    <main id="main-content" tabindex="-1">${content}</main>
    <div class="live-region" data-live aria-live="polite"></div>
  </div>`;
}

function renderLoading(): string {
  return shell(`<section class="status-view" aria-busy="true">
    <i class="spin" data-lucide="loader-circle" aria-hidden="true"></i>
    <h1>正在打开镜头</h1>
    <p>稍等一下，马上就好。</p>
  </section>`, { compact: true });
}

function renderCreate(): string {
  const current = state.mode === "create" ? state : { submitting: false, error: null };
  return shell(`<section class="create-view">
    <div class="intro-copy">
      <p class="kicker">别人眼中的你</p>
      <h1>朋友们，会怎么看你？</h1>
      <p>邀请熟悉你的人，从 10 个日常片段里选出他们眼中的你。</p>
    </div>
    ${rolesMosaic()}
    <form class="create-form" data-form="create" novalidate>
      <label for="display-name">朋友平时怎么叫你？</label>
      <input id="display-name" name="displayName" autocomplete="nickname" maxlength="12" placeholder="例如：小杨" aria-describedby="create-help create-error" required />
      <p id="create-help" class="field-help">这个称呼会出现在朋友看到的题目里。</p>
      <p id="create-error" class="field-error" role="alert">${escapeHtml(current.error ?? "")}</p>
      <button class="primary-button" type="submit" ${current.submitting ? "disabled" : ""}>
        ${current.submitting ? '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>正在准备邀请</span>' : '<span>邀请朋友来回答</span><i data-lucide="arrow-right" aria-hidden="true"></i>'}
      </button>
    </form>
  </section>`);
}

function renderInvite(): string {
  if (state.mode !== "invite") return "";
  const { created } = state;
  return shell(`<section class="invite-view">
    <div class="success-mark" aria-hidden="true"><i data-lucide="check"></i></div>
    <p class="kicker">邀请已准备好</p>
    <h1>${escapeHtml(created.profile.displayName)}，让朋友说说他们眼中的你</h1>
    <p>把公开链接发给熟悉你的朋友。每个人独立回答，看不到其他人的选择。</p>
    <div class="link-box"><span>${escapeHtml(created.publicUrl)}</span>${iconButton("copy-link", "copy", "复制公开链接")}</div>
    <div class="button-stack">
      <button class="primary-button" type="button" data-action="share-invite"><i data-lucide="share-2" aria-hidden="true"></i><span>分享邀请</span></button>
      <button class="secondary-button" type="button" data-action="save-owner-link"><i data-lucide="key-round" aria-hidden="true"></i><span>保存私密管理入口</span></button>
      <button class="text-button centered" type="button" data-action="open-results"><i data-lucide="lock-keyhole" aria-hidden="true"></i><span>查看朋友眼中的我</span></button>
    </div>
    <p class="privacy-note">换浏览器或设备时，用私密管理入口恢复结果。它只供你使用，不要转发给朋友。</p>
  </section>`, { compact: true });
}

function renderParticipantStart(): string {
  if (state.mode !== "participant-start") return "";
  const { profile, nickname, consent, error } = state;
  return shell(`<section class="participant-view">
    <div class="participant-heading">
      <p class="kicker">你眼中的 TA</p>
      <h1>朋友镜头里的<br />${escapeHtml(profile.displayName)}</h1>
      <p>用 10 个日常片段，留下你对 TA 的真实印象。</p>
    </div>
    ${rolesMosaic()}
    <form class="participant-form" data-form="participant" novalidate>
      <label for="friend-nickname">你的朋友昵称</label>
      <input id="friend-nickname" name="friendNickname" autocomplete="nickname" maxlength="12" value="${escapeHtml(nickname)}" placeholder="TA 能认出的称呼" aria-describedby="participant-help participant-error" required />
      <p id="participant-help" class="field-help">昵称和答案会由系统保存，但 TA 看不到你是谁或你怎么选。</p>
      <label class="consent-row">
        <input type="checkbox" name="privacyConsent" ${consent ? "checked" : ""} required />
        <span>我已了解并同意以上保存方式</span>
      </label>
      <p id="participant-error" class="field-error" role="alert">${escapeHtml(error ?? "")}</p>
      <button class="primary-button" type="submit"><span>开始留下镜头</span><i data-lucide="arrow-right" aria-hidden="true"></i></button>
    </form>
  </section>`);
}

function renderQuiz(): string {
  if (state.mode !== "quiz") return "";
  const { profile, draft, stage, error } = state;
  if (stage === "review" || stage === "submitting" || stage === "submit-error") {
    return shell(`<section class="review-view">
      <div class="success-mark" aria-hidden="true"><i data-lucide="check"></i></div>
      <p class="kicker">10 个片段已完成</p>
      <h1>把这组镜头交给 ${escapeHtml(profile.displayName)}</h1>
      <p>提交后，TA 只会看到朋友们的整体印象，看不到你的昵称和单独答案。</p>
      <p class="field-error" role="alert">${escapeHtml(error ?? "")}</p>
      <div class="button-stack">
        <button class="primary-button" type="button" data-action="submit-quiz" ${stage === "submitting" ? "disabled" : ""}>
          ${stage === "submitting" ? '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>正在收录</span>' : '<span>确认提交</span><i data-lucide="arrow-right" aria-hidden="true"></i>'}
        </button>
        <button class="secondary-button" type="button" data-action="back-to-last" ${stage === "submitting" ? "disabled" : ""}><i data-lucide="arrow-left" aria-hidden="true"></i><span>返回检查</span></button>
      </div>
    </section>`, { back: "back-to-last", compact: true });
  }

  const question = QUESTIONS[draft.current];
  if (!question) return renderErrorContent("找不到这道题，请重新打开页面");
  const selected = draft.answers[draft.current];
  return shell(`<section class="quiz-view">
    <div class="quiz-progress">
      <span>第 ${draft.current + 1} / ${QUIZ_LENGTH} 题</span>
      <progress value="${draft.current + 1}" max="${QUIZ_LENGTH}">${draft.current + 1} / ${QUIZ_LENGTH}</progress>
    </div>
    <form class="question-form" data-form="question">
      <fieldset>
        <legend>${escapeHtml(question.prompt)}</legend>
        <div class="option-list">
          ${question.options.map((option, index) => `<label class="option-row${selected === option.id ? " is-selected" : ""}">
            <input type="radio" name="option" value="${option.id}" ${selected === option.id ? "checked" : ""} />
            <span class="option-letter" aria-hidden="true">${String.fromCharCode(65 + index)}</span>
            <span>${escapeHtml(option.text)}</span>
            <i data-lucide="check" aria-hidden="true"></i>
          </label>`).join("")}
        </div>
      </fieldset>
    </form>
    <div class="quiz-actions">
      <button class="text-button" type="button" data-action="previous-question" ${draft.current === 0 ? "disabled" : ""}><i data-lucide="arrow-left" aria-hidden="true"></i><span>上一题</span></button>
      <span>${escapeHtml(profile.displayName)}</span>
    </div>
  </section>`, { back: "exit-quiz" });
}

function renderThanks(): string {
  if (state.mode !== "thanks") return "";
  return shell(`<section class="thanks-view">
    <div class="thanks-art role-color--warmth">
      <img src="${ROLE_BY_ID.warmth.image}" width="384" height="384" alt="${ROLE_BY_ID.warmth.name}" />
    </div>
    <p class="kicker">${state.alreadyDone ? "已经收录" : "镜头已收录"}</p>
    <h1>${state.alreadyDone ? "你已经为 TA 留下过镜头" : "谢谢你留下这一眼"}</h1>
    <p>${escapeHtml(state.profile.displayName)} 只会看到朋友们汇成的整体印象。</p>
    <button class="primary-button" type="button" data-action="create-mine"><span>也创建我的页面</span><i data-lucide="arrow-right" aria-hidden="true"></i></button>
  </section>`, { compact: true });
}

function sortedRoles(results: Extract<ResultsResponse, { locked: false }>): Array<{ role: RoleDefinition; percent: number }> {
  return ROLE_IDS.map((id, index) => ({ role: ROLE_BY_ID[id], percent: results.percentages[id], index }))
    .sort((a, b) => b.percent - a.percent || a.index - b.index)
    .map(({ role, percent }) => ({ role, percent }));
}

function renderManage(): string {
  if (state.mode !== "manage") return "";
  const { results, feedback, deleteOpen, deleting } = state;
  const invite = publicUrl(state.slug);
  if (results.locked) {
    const remaining = Math.max(0, results.requiredCount - results.responseCount);
    return shell(`<section class="manage-view">
      <div class="lock-visual" aria-hidden="true"><i data-lucide="lock-keyhole"></i></div>
      <p class="kicker">${escapeHtml(results.profile.displayName)} 的朋友镜头</p>
      <h1>再等 ${remaining} 位朋友，完整结果就会出现</h1>
      <div class="response-count"><strong>${results.responseCount}</strong><span>/ ${results.requiredCount} 份有效回答</span></div>
      <progress value="${results.responseCount}" max="${results.requiredCount}">${results.responseCount} / ${results.requiredCount}</progress>
      <p>解锁前只显示人数，不会根据少量答案提前推断角色。</p>
      <div class="button-stack">
        <button class="primary-button" type="button" data-action="share-manage"><i data-lucide="share-2" aria-hidden="true"></i><span>继续邀请朋友</span></button>
        <button class="secondary-button" type="button" data-action="refresh-results"><i data-lucide="refresh-cw" aria-hidden="true"></i><span>刷新结果</span></button>
        <button class="text-button centered" type="button" data-action="save-owner-link"><i data-lucide="key-round" aria-hidden="true"></i><span>保存私密管理入口</span></button>
      </div>
      <div class="link-box"><span>${escapeHtml(invite)}</span>${iconButton("copy-manage-link", "copy", "复制公开链接")}</div>
      <button class="danger-link" type="button" data-action="open-delete"><i data-lucide="trash-2" aria-hidden="true"></i><span>删除这个页面</span></button>
      ${deleteDialog(deleteOpen, deleting)}
      <p class="inline-feedback" aria-live="polite">${escapeHtml(feedback ?? "")}</p>
    </section>`, { compact: true });
  }

  const ordered = sortedRoles(results);
  const lead = ordered[0];
  if (!lead) return renderErrorContent("结果暂时不可用");
  return shell(`<section class="results-view">
    <p class="kicker">朋友镜头里的 ${escapeHtml(results.profile.displayName)}</p>
    <div class="lead-result role-color--${lead.role.id}">
      <img src="${lead.role.image}" width="384" height="384" alt="${lead.role.name}" />
      <div><span>主镜头</span><strong>${lead.percent}%</strong><h1>${lead.role.name}</h1></div>
    </div>
    <div class="role-results" aria-label="六种角色聚合结果">
      ${ordered.map(({ role, percent }) => `<div class="result-row role-color--${role.id}">
        <span class="result-swatch" aria-hidden="true"></span>
        <span><strong>${role.name}</strong><small>${role.description}</small></span>
        <b>${percent}%</b>
      </div>`).join("")}
    </div>
    <p class="result-caption"><i data-lucide="users-round" aria-hidden="true"></i>来自 ${results.responseCount} 位朋友对你匿名的印象</p>
    <div class="button-stack">
      <button class="primary-button" type="button" data-action="share-result"><i data-lucide="share-2" aria-hidden="true"></i><span>分享结果卡</span></button>
      <button class="secondary-button" type="button" data-action="download-result"><i data-lucide="download" aria-hidden="true"></i><span>保存图片</span></button>
      <button class="text-button centered" type="button" data-action="refresh-results"><i data-lucide="refresh-cw" aria-hidden="true"></i><span>刷新数据</span></button>
      <button class="text-button centered" type="button" data-action="save-owner-link"><i data-lucide="key-round" aria-hidden="true"></i><span>保存私密管理入口</span></button>
    </div>
    <div class="link-box"><span>${escapeHtml(invite)}</span>${iconButton("copy-manage-link", "copy", "复制公开链接")}</div>
    <button class="danger-link" type="button" data-action="open-delete"><i data-lucide="trash-2" aria-hidden="true"></i><span>删除这个页面</span></button>
    ${deleteDialog(deleteOpen, deleting)}
    <p class="inline-feedback" aria-live="polite">${escapeHtml(feedback ?? "")}</p>
  </section>`);
}

function deleteDialog(open: boolean, deleting: boolean): string {
  return `<dialog class="delete-dialog" data-delete-dialog data-should-open="${open ? "true" : "false"}">
    <div class="dialog-heading">${iconButton("close-delete", "x", "关闭")}</div>
    <h2>删除整个页面？</h2>
    <p>朋友昵称、10 道原始答案和聚合结果都会永久删除，无法恢复。</p>
    <div class="button-stack">
      <button class="danger-button" type="button" data-action="confirm-delete" ${deleting ? "disabled" : ""}>${deleting ? "正在删除" : "确认永久删除"}</button>
      <button class="secondary-button" type="button" data-action="close-delete" ${deleting ? "disabled" : ""}>取消</button>
    </div>
  </dialog>`;
}

function renderUnauthorized(): string {
  return shell(`<section class="status-view">
    <i data-lucide="lock-keyhole" aria-hidden="true"></i>
    <h1>需要私密管理入口</h1>
    <p>请打开创建时保存的私密管理链接恢复权限。公开邀请链接仍可继续收集回答。</p>
    <a class="primary-button" href="/"><span>创建我的页面</span><i data-lucide="arrow-right" aria-hidden="true"></i></a>
  </section>`, { compact: true });
}

function renderErrorContent(message: string): string {
  return shell(`<section class="status-view">
    <h1>暂时没能打开</h1>
    <p>${escapeHtml(message)}</p>
    <button class="primary-button" type="button" data-action="retry"><i data-lucide="refresh-cw" aria-hidden="true"></i><span>重新加载</span></button>
    <button class="text-button centered" type="button" data-action="create-mine">创建我的页面</button>
  </section>`, { compact: true });
}

function render(): void {
  clearChoiceTimer();
  const content = state.mode === "loading" ? renderLoading()
    : state.mode === "create" ? renderCreate()
      : state.mode === "invite" ? renderInvite()
        : state.mode === "participant-start" ? renderParticipantStart()
          : state.mode === "quiz" ? renderQuiz()
            : state.mode === "thanks" ? renderThanks()
              : state.mode === "manage" ? renderManage()
                : state.mode === "unauthorized" ? renderUnauthorized()
                  : state.mode === "unavailable" ? renderErrorContent(state.message)
                    : renderErrorContent(state.message);
  app.innerHTML = content;
  createIcons({
    icons: { ArrowLeft, ArrowRight, Check, Copy, Download, KeyRound, LoaderCircle, LockKeyhole, RefreshCw, Share2, Trash2, UsersRound, X },
    attrs: { "stroke-width": 1.8 },
  });
  if (state.mode === "manage" && state.deleteOpen) {
    const dialog = document.querySelector<HTMLDialogElement>("[data-delete-dialog]");
    if (dialog && !dialog.open) dialog.showModal();
  }
}

function focusMain(): void {
  window.scrollTo(0, 0);
  requestAnimationFrame(() => document.querySelector<HTMLElement>("#main-content")?.focus({ preventScroll: true }));
}

async function loadRoute(route = readRoute()): Promise<void> {
  consumeOwnerAccess(route);
  state = { mode: "loading", route };
  render();
  if (route.kind === "create") {
    state = { mode: "create", submitting: false, error: null };
    render();
    return;
  }
  if (route.kind === "participate") {
    try {
      const profile = await getPublicProfile(route.slug);
      if (localStorage.getItem(doneKey(route.slug)) === "1") {
        state = { mode: "thanks", profile, alreadyDone: true };
      } else {
        const draft = readDraft(route.slug);
        state = {
          mode: "participant-start",
          profile,
          nickname: draft?.nickname ?? "",
          consent: draft?.consent ?? false,
          error: null,
        };
      }
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "这个镜头暂时打不开";
      state = caught instanceof ApiError && caught.status === 404
        ? { mode: "unavailable", message }
        : { mode: "error", route, message };
    }
    render();
    focusMain();
    return;
  }
  const ownerToken = localStorage.getItem(ownerKey(route.slug));
  if (!ownerToken) {
    state = { mode: "unauthorized", slug: route.slug };
    render();
    return;
  }
  try {
    const results = await getResults(route.slug, ownerToken);
    state = { mode: "manage", slug: route.slug, results, feedback: null, deleteOpen: false, deleting: false };
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 403) {
      localStorage.removeItem(ownerKey(route.slug));
      state = { mode: "unauthorized", slug: route.slug };
    } else {
      state = { mode: "error", route, message: caught instanceof Error ? caught.message : "结果暂时打不开" };
    }
  }
  render();
  focusMain();
}

function setLive(message: string): void {
  const region = document.querySelector<HTMLElement>("[data-live]");
  if (region) region.textContent = message;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement("textarea");
  input.value = value;
  input.className = "clipboard-fallback";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  input.remove();
}

async function shareLink(slug: string, displayName: string): Promise<void> {
  const url = publicUrl(slug);
  if (navigator.share) {
    await navigator.share({ title: `你眼中的 ${displayName}`, text: `从 10 个日常片段里，选出你眼中的 ${displayName}。`, url });
  } else {
    await copyText(url);
    setLive("公开链接已复制");
  }
}

async function savePrivateManagementLink(slug: string, ownerToken: string, displayName: string): Promise<void> {
  const url = privateManagementUrl(slug, ownerToken);
  if (navigator.share) {
    try {
      await navigator.share({
        title: `${displayName} 的私密管理入口`,
        text: "只供你查看朋友回答，请勿转发。",
        url,
      });
      setLive("私密管理入口已打开分享，请保存好");
      return;
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") {
        setLive("保存已取消");
        return;
      }
    }
  }
  await copyText(url);
  setLive("私密管理入口已复制，请保存到微信收藏或备忘录");
}

function answersFromDraft(draft: QuizDraft): RawAnswer[] {
  return QUESTIONS.map((question, index) => ({ questionId: question.id, optionId: draft.answers[index] ?? "" }));
}

async function refreshManage(slug: string): Promise<void> {
  const token = localStorage.getItem(ownerKey(slug));
  if (!token) {
    state = { mode: "unauthorized", slug };
    render();
    return;
  }
  state = { mode: "loading", route: { kind: "manage", slug } };
  render();
  await loadRoute({ kind: "manage", slug });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("角色图片加载失败"));
    image.src = src;
  });
}

async function resultBlob(results: Extract<ResultsResponse, { locked: false }>): Promise<Blob> {
  const ordered = sortedRoles(results);
  const lead = ordered[0];
  if (!lead) throw new Error("结果暂时不可用");
  const image = await loadImage(lead.role.image);
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1440;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("无法生成结果图片");
  context.fillStyle = "#F7F7F2";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = lead.role.color;
  context.fillRect(64, 64, 952, 620);
  context.drawImage(image, 528, 112, 420, 420);
  context.fillStyle = "#181817";
  context.font = "500 38px system-ui, sans-serif";
  context.fillText(`朋友镜头里的 ${results.profile.displayName}`, 112, 150);
  context.font = "800 120px system-ui, sans-serif";
  context.fillText(`${lead.percent}%`, 112, 330);
  context.font = "800 64px system-ui, sans-serif";
  context.fillText(lead.role.name, 112, 425);
  context.font = "400 34px system-ui, sans-serif";
  context.fillText(lead.role.description, 112, 490);
  ordered.slice(1, 4).forEach(({ role, percent }, index) => {
    const y = 780 + index * 150;
    context.fillStyle = role.color;
    context.fillRect(72, y - 48, 32, 104);
    context.fillStyle = "#181817";
    context.font = "700 42px system-ui, sans-serif";
    context.fillText(role.name, 136, y);
    context.font = "800 52px system-ui, sans-serif";
    context.textAlign = "right";
    context.fillText(`${percent}%`, 944, y);
    context.textAlign = "left";
  });
  context.fillStyle = "#65655F";
  context.font = "400 30px system-ui, sans-serif";
  context.fillText(`来自 ${results.responseCount} 位朋友对你匿名的印象`, 72, 1300);
  context.fillStyle = "#181817";
  context.font = "700 30px system-ui, sans-serif";
  context.fillText("朋友镜头里的我", 72, 1360);
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("无法生成结果图片")), "image/png"));
}

async function exportResult(mode: "share" | "download"): Promise<void> {
  if (state.mode !== "manage" || state.results.locked) return;
  const unlockedResults = state.results;
  const snapshot = state;
  try {
    setLive("正在生成结果图片");
    const blob = await resultBlob(unlockedResults);
    const filename = `朋友镜头里的${unlockedResults.profile.displayName}.png`;
    const file = new File([blob], filename, { type: "image/png" });
    if (mode === "share" && navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: `朋友镜头里的 ${unlockedResults.profile.displayName}`, url: publicUrl(snapshot.slug) });
      setLive("结果卡已打开分享")
      return;
    }
    const link = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = filename;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    if (mode === "share") await copyText(publicUrl(snapshot.slug));
    setLive(mode === "share" ? "结果图片已保存，公开链接已复制" : "结果图片已保存");
  } catch (caught) {
    setLive(caught instanceof Error ? caught.message : "暂时无法生成图片");
  }
}

app.addEventListener("submit", (event) => {
  event.preventDefault();
  const form = event.target as HTMLFormElement;
  if (form.dataset.form === "create" && state.mode === "create") {
    const input = form.elements.namedItem("displayName") as HTMLInputElement;
    const displayName = input.value.trim();
    if (!displayName) {
      state = { ...state, error: "请输入朋友平时怎么叫你" };
      render();
      document.querySelector<HTMLInputElement>("#display-name")?.focus();
      return;
    }
    state = { ...state, submitting: true, error: null };
    render();
    void createProfile(displayName).then((created) => {
      localStorage.setItem(ownerKey(created.profile.slug), created.ownerToken);
      history.replaceState({}, "", `/?manage=${encodeURIComponent(created.profile.slug)}`);
      state = { mode: "invite", created };
      render();
      focusMain();
    }).catch((caught: unknown) => {
      state = { mode: "create", submitting: false, error: caught instanceof Error ? caught.message : "暂时无法生成链接" };
      render();
    });
  }

  if (form.dataset.form === "participant" && state.mode === "participant-start") {
    const nicknameInput = form.elements.namedItem("friendNickname") as HTMLInputElement;
    const consentInput = form.elements.namedItem("privacyConsent") as HTMLInputElement;
    const nickname = nicknameInput.value.trim();
    if (!nickname) {
      state = { ...state, nickname, consent: consentInput.checked, error: "请输入你的朋友昵称" };
      render();
      document.querySelector<HTMLInputElement>("#friend-nickname")?.focus();
      return;
    }
    if (!consentInput.checked) {
      state = { ...state, nickname, consent: false, error: "请先确认昵称和答案的保存说明" };
      render();
      document.querySelector<HTMLInputElement>('input[name="privacyConsent"]')?.focus();
      return;
    }
    const saved = readDraft(state.profile.slug);
    const draft: QuizDraft = saved ?? { version: 1, nickname, consent: true, current: 0, answers: Array<string | null>(QUIZ_LENGTH).fill(null) };
    draft.nickname = nickname;
    saveDraft(state.profile.slug, draft);
    state = { mode: "quiz", profile: state.profile, draft, stage: "answering", error: null };
    render();
    focusMain();
  }
});

app.addEventListener("change", (event) => {
  const input = event.target as HTMLInputElement;
  if (input.name === "option" && state.mode === "quiz" && state.stage === "answering") {
    const draft: QuizDraft = { ...state.draft, answers: [...state.draft.answers] };
    draft.answers[draft.current] = input.value;
    saveDraft(state.profile.slug, draft);
    state = { ...state, draft };
    render();
    choiceTimer = window.setTimeout(() => {
      if (state.mode !== "quiz" || state.stage !== "answering") return;
      const next = state.draft.current + 1;
      if (next >= QUIZ_LENGTH) {
        state = { ...state, stage: "review", error: null };
      } else {
        const draftNext = { ...state.draft, current: next };
        saveDraft(state.profile.slug, draftNext);
        state = { ...state, draft: draftNext };
      }
      render();
      focusMain();
    }, 190);
  }
});

app.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
  if (!button) return;
  const action = button.dataset.action;
  if (action === "go-create" || action === "create-mine") {
    event.preventDefault();
    history.pushState({}, "", "/");
    void loadRoute({ kind: "create" });
  } else if (action === "copy-link" && state.mode === "invite") {
    void copyText(state.created.publicUrl).then(() => setLive("公开链接已复制"));
  } else if (action === "share-invite" && state.mode === "invite") {
    void shareLink(state.created.profile.slug, state.created.profile.displayName).catch(() => setLive("分享已取消"));
  } else if (action === "save-owner-link" && state.mode === "invite") {
    void savePrivateManagementLink(state.created.profile.slug, state.created.ownerToken, state.created.profile.displayName)
      .catch(() => setLive("暂时无法保存管理入口"));
  } else if (action === "save-owner-link" && state.mode === "manage") {
    const token = localStorage.getItem(ownerKey(state.slug));
    if (!token) {
      setLive("当前没有可保存的管理凭证");
    } else {
      void savePrivateManagementLink(state.slug, token, state.results.profile.displayName)
        .catch(() => setLive("暂时无法保存管理入口"));
    }
  } else if (action === "open-results" && state.mode === "invite") {
    void refreshManage(state.created.profile.slug);
  } else if (action === "previous-question" && state.mode === "quiz" && state.stage === "answering" && state.draft.current > 0) {
    const draft = { ...state.draft, current: state.draft.current - 1 };
    saveDraft(state.profile.slug, draft);
    state = { ...state, draft };
    render();
  } else if (action === "exit-quiz" && state.mode === "quiz") {
    state = { mode: "participant-start", profile: state.profile, nickname: state.draft.nickname, consent: true, error: null };
    render();
  } else if (action === "back-to-last" && state.mode === "quiz") {
    const draft = { ...state.draft, current: QUIZ_LENGTH - 1 };
    state = { ...state, draft, stage: "answering", error: null };
    render();
  } else if (action === "submit-quiz" && state.mode === "quiz" && state.stage !== "submitting") {
    const snapshot = state;
    state = { ...state, stage: "submitting", error: null };
    render();
    void submitResponse(snapshot.profile.slug, snapshot.draft.nickname, getRespondentToken(snapshot.profile.slug), answersFromDraft(snapshot.draft))
      .then(() => {
        localStorage.setItem(doneKey(snapshot.profile.slug), "1");
        sessionStorage.removeItem(draftKey(snapshot.profile.slug));
        state = { mode: "thanks", profile: snapshot.profile, alreadyDone: false };
        render();
        focusMain();
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.code === "ALREADY_SUBMITTED") {
          localStorage.setItem(doneKey(snapshot.profile.slug), "1");
          sessionStorage.removeItem(draftKey(snapshot.profile.slug));
          state = { mode: "thanks", profile: snapshot.profile, alreadyDone: true };
        } else {
          state = { ...snapshot, stage: "submit-error", error: caught instanceof Error ? caught.message : "暂时无法提交" };
        }
        render();
      });
  } else if ((action === "share-manage" || action === "share-result") && state.mode === "manage") {
    if (action === "share-result" && !state.results.locked) void exportResult("share");
    else void shareLink(state.slug, state.results.profile.displayName).catch(() => setLive("分享已取消"));
  } else if (action === "download-result") {
    void exportResult("download");
  } else if (action === "copy-manage-link" && state.mode === "manage") {
    void copyText(publicUrl(state.slug)).then(() => setLive("公开链接已复制"));
  } else if (action === "refresh-results" && state.mode === "manage") {
    void refreshManage(state.slug);
  } else if (action === "open-delete" && state.mode === "manage") {
    state = { ...state, deleteOpen: true };
    render();
  } else if (action === "close-delete" && state.mode === "manage" && !state.deleting) {
    state = { ...state, deleteOpen: false };
    render();
    requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[data-action="open-delete"]')?.focus());
  } else if (action === "confirm-delete" && state.mode === "manage" && !state.deleting) {
    const snapshot = state;
    const token = localStorage.getItem(ownerKey(snapshot.slug));
    if (!token) return;
    state = { ...snapshot, deleting: true };
    render();
    void deleteProfile(snapshot.slug, token).then(() => {
      localStorage.removeItem(ownerKey(snapshot.slug));
      state = { mode: "unavailable", message: "这个页面和全部朋友答案已经删除" };
      render();
    }).catch((caught: unknown) => {
      state = { ...snapshot, feedback: caught instanceof Error ? caught.message : "暂时无法删除", deleteOpen: false, deleting: false };
      render();
    });
  } else if (action === "retry") {
    const route = state.mode === "error" ? state.route : readRoute();
    void loadRoute(route);
  }
});

app.addEventListener("cancel", (event) => {
  if (state.mode !== "manage" || !state.deleteOpen || state.deleting) return;
  event.preventDefault();
  state = { ...state, deleteOpen: false };
  render();
  requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[data-action="open-delete"]')?.focus());
});

window.addEventListener("popstate", () => void loadRoute());
void loadRoute();
